import { ChangeDetectionStrategy, Component, effect, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ChartOptions } from '../dashboard-chart/chart-type-options';
import { DashboardChartComponent } from '../dashboard-chart/dashboard-chart.component';
import { ChartType, WidgetConfiguration } from './dashboard-widget.types';

/**
 * Controls the widget options and pass through the chart config
 */
@Component({
    selector: 'dashboard-widget',
    standalone: true,
    imports: [FormsModule, DashboardChartComponent, SelectModule, ButtonModule],
    templateUrl: './dashboard-widget.component.html',
    styleUrl: './dashboard-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardWidgetComponent implements OnInit {
    /** Chart instance control */
    public chartInstance!: Highcharts.Chart;
    /** Input for widget configuration with data and type */
    public widgetConfiguration = input<WidgetConfiguration | undefined>(undefined);
    /** Control for data table visualization */
    protected dataTableVisible = false;
    /** Chart type control */
    private _defaultChartType: ChartType = 'bar';
    protected chartType = signal<ChartType>(this._defaultChartType);
    /** Widget configuration used in the component - input + component manipulations */
    protected componentWidgetConfig = signal<WidgetConfiguration | undefined>(undefined);

    // Chart types, computed from ChartOptions, for select dropdown, creating title case labels
    protected chartTypes = Object.keys(ChartOptions)
        .filter(key => key !== 'base')
        .map(key => {
            const label = key
                .replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            return {
                label,
                value: key
            };
        });

    constructor() {
        effect(() => {
            const updateChartConfig = this.widgetConfiguration();
            if (updateChartConfig) this.componentWidgetConfig.set({ ...updateChartConfig, chartType: this.chartType() });
        });
    }

    public ngOnInit(): void {
        // Initialize visualization configuration from input
        this.onChartTypeChange(this.widgetConfiguration()?.chartType ?? this._defaultChartType);
    }

    public closeWidget() {
        //TODO: close the widget
    }

    public onChartReady(chart: Highcharts.Chart) {
        this.chartInstance = chart;
    }

    public printChart() {
        this.chartInstance?.exporting.print();
    }

    public toggleDataTable() {
        if (this.chartInstance) {
            const chartOptions = this.chartInstance.options;
            if (chartOptions.exporting) {
                this.dataTableVisible = !this.dataTableVisible;
                chartOptions.exporting.showTable = this.dataTableVisible;
                this.chartInstance.update(chartOptions);
                this.chartInstance.redraw();
            }
        }
    }

    public toggleFullscreen() {
        this.chartInstance?.fullscreen?.toggle();
    }

    protected onChartTypeChange(type: ChartType): void {
        const currentChartConfig = this.widgetConfiguration();
        if (!currentChartConfig) return;
        this.chartType.set(type);
        this.componentWidgetConfig.set({ ...currentChartConfig, chartType: type });
    }

    /**
     * Exports the chart in the specified format
     * @param type - The export format (e.g., 'application/pdf', 'image/png')
     */
    private exportChart(type: string): void {
        // FIXME: this is not quite right
        // this.chartInstance?.exportChart({
        //     type: type
        // });
    }
}
